import { Component } from 'react';
import { toast } from 'react-toastify';
import {
  Row,
  Col,
  Card,
  ListGroup,
  ListGroupItem,
  Dropdown,
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import Layout from '../DashboardLayout';
import Loader from '../../components/Loader';
import { CreateRole, EditRole } from '../../components/rolesAndPermissions';
import { WithConfirmation } from '../../components/common';
import { roleAndPermissionsService, countrySiteService } from '../../services';
import { API_FAILURE_ERROR, CUSTOM_ROLE } from '../../constants';

// Delete Section Link
const DeleteRole = ({ handleShow }) => (
  <Dropdown.Item onClick={handleShow}>Delete</Dropdown.Item>
);
DeleteRole.propTypes = {
  handleShow: PropTypes.func,
};

// HOC for task deletion confirmation
const DeleteButton = ({ deleteProp }) => {
  const DeleteRoleWithConfirmation = WithConfirmation(DeleteRole);
  const DeleteModalProps = {
    title: `Role Deletion`,
    action: deleteProp,
    content: [
      `You're about to delete role. All data will be permanently deleted. You will not be able to restore it later. Do you want to delete it?`,
    ],
    actionBtnText: 'Delete',
  };
  return <DeleteRoleWithConfirmation modalProps={DeleteModalProps} />;
};
DeleteButton.propTypes = {
  deleteProp: PropTypes.func,
};

class RolesAndPermissions extends Component {
  pageHeading = {
    preTitle: 'Manage',
    title: 'Roles and Permissions',
  };

  state = {
    isLoading: false,
    isLoadingRoles: false,
    roles: [],
    showCreateRole: false,
    createPermissionList: [],
    createTypesList: [],
    editRoleId: '',
    showUpdateRole: false,
    role: null,
    editPermissionList: [],
    editSelectedPermissionList: [],
    editTypeList: [],
    editRoleName: '',
    userTypesList: [],
    sitesList: [],
    siteAccessInfo: {},
  };

  componentDidMount() {
    this.handleGetRolesList();
    this.handleGetSitesList();
    roleAndPermissionsService
      .get_user_types()
      .then((userTypesResponse) => {
        const userTypesListResponse =
          (userTypesResponse && userTypesResponse.data) || [];
        this.setState({
          userTypesList: userTypesListResponse.map((item) => item.label),
        });
      })
      .catch((errResponse) => {
        this.handleLoader(false);
        if ('message' in errResponse) {
          toast.error(errResponse.message);
        } else {
          toast.error(API_FAILURE_ERROR);
        }
      });
  }

  getPermissions = (status) => {
    const { userTypesList } = this.state;
    this.handleLoader(true);
    roleAndPermissionsService
      .get_permissions_data()
      .then((res) => {
        if (res && res.data && res.data.length) {
          const additionalPermissionsList = res.data;
          const permissionsListFilter = additionalPermissionsList.filter(
            (item) => {
              item.permissions = item.permissions
                .filter((it) => !it.isDefault)
                .filter((itm) => itm.isHidden !== true);
              return item;
            }
          );
          this.setState({
            createTypesList: permissionsListFilter
              .map((item) => {
                const serialNumber =
                  userTypesList.indexOf(item.userTypes.typeName) + 1;
                return {
                  value: item.userTypes.typeId.toLowerCase(),
                  label:
                    item.userTypes.typeName.toLowerCase()[0].toUpperCase() +
                    item.userTypes.typeName.toLowerCase().substring(1),
                  serialNumber,
                };
              })
              .sort((a, b) => a.serialNumber - b.serialNumber),
          });

          const permissionUiBindFormat = permissionsListFilter.map((item) => {
            const permissionList = JSON.parse(JSON.stringify(item.permissions));

            const groupByModuleName = Object.groupBy(
              permissionList,
              ({ moduleName }) => moduleName
            );
            const groupByObjectKey = Object.keys(groupByModuleName).map(
              (itemname) => ({
                name: itemname,
                list: groupByModuleName[itemname],
              })
            );

            return {
              typeName: item.userTypes.typeName,
              typeId: item.userTypes.typeId,
              permissions: groupByObjectKey,
            };
          });
          this.setState({
            createPermissionList: permissionUiBindFormat,
            showCreateRole: status,
          });
          this.handleLoader(false);
        }
      })
      .catch((errResponse) => {
        this.handleLoader(false);
        if ('message' in errResponse) {
          toast.error(errResponse.message);
        } else {
          toast.error(API_FAILURE_ERROR);
        }
      });
  };

  handleGetSitesList = async () => {
    this.setState((state) => ({
      ...state,
      isLoading: true,
    }));
    countrySiteService
      .get_sites_list({
        teamOwnerId: localStorage.getItem('teamOwnerId'),
        listAllSites: true,
      })
      .then((response) => {
        if (response.data) {
          this.setState((state) => ({
            ...state,
            sitesList: response.data,
          }));
        }
      })
      .finally(() => {
        this.setState((state) => ({
          ...state,
          isLoading: false,
        }));
      });
  };

  handleLoader = (pageState) => {
    this.setState((state) => ({
      ...state,
      isLoading: pageState,
    }));
  };

  handleLoaderRoles = (pageState) => {
    this.setState((state) => ({
      ...state,
      isLoadingRoles: pageState,
    }));
  };

  handleGetRolesList = () => {
    this.handleLoaderRoles(true);
    roleAndPermissionsService.get_roles().then(
      (res) => {
        if (res && res.data && res.data.length) {
          this.setState({ roles: res.data });
        }
        this.handleLoaderRoles(false);
      },
      (errResponse) => {
        this.handleLoaderRoles(false);
        if ('message' in errResponse) {
          toast.error(errResponse.message);
        } else {
          toast.error(API_FAILURE_ERROR);
        }
      }
    );
  };

  handleCreateRole = (formValues) => {
    const { createTypesList } = this.state;
    const mangerType =
      createTypesList?.length > 0 &&
      createTypesList.find((item) => item.label === 'Manager');
    this.handleLoader(true);
    const addRoleValue = {
      roleName: formValues.roleName,
      accountOwnerId: localStorage.getItem('teamOwnerId'),
      extraPermissions: formValues.selectedPermissions,
      userType: formValues.userType,
    };
    if (formValues.userType === mangerType.value) {
      if (formValues.siteId === 'all') {
        addRoleValue.siteAccessInfo = {
          isGlobal: true,
        };
      } else if (formValues.siteId?.length > 0) {
        addRoleValue.siteAccessInfo = {
          siteIds: [...formValues.siteId],
        };
      } else {
        addRoleValue.siteAccessInfo = {
          siteIds: [],
        };
      }
    }

    roleAndPermissionsService.create_role(addRoleValue).then(
      (res) => {
        if (res && res.status) {
          this.handleGetRolesList();
          toast.success(CUSTOM_ROLE.CREATE_ROLE);
          this.handleShowCreateRole(false);
        }
        this.handleLoader(false);
      },
      (errResponse) => {
        this.handleLoader(false);

        if ('message' in errResponse) {
          toast.error(errResponse.message);
        } else {
          toast.error(API_FAILURE_ERROR);
        }
      }
    );
  };

  handleShowCreateRole = (status) => {
    if (status) {
      this.getPermissions(status);
    } else {
      this.setState({ showCreateRole: status });
    }
  };

  handleUpdateRole = (values) => {
    const { editTypesList } = this.state;
    const mangerType =
      editTypesList?.length > 0 &&
      editTypesList.find((item) => item.label === 'Manager');
    this.handleLoader(true);
    const updateValues = {
      roleId: values.editRoleId,
      roleName: values.roleName,
      teamOwnerId: localStorage.getItem('teamOwnerId'),
      permissions: values.selectedPermissions,
      userType: values.userType,
      siteAccessInfo: {
        siteIds: [],
      },
    };

    if (values.userType === mangerType.value) {
      if (values.siteId?.length > 0) {
        updateValues.siteAccessInfo = {
          siteIds: [...values.siteId],
        };
      } else if (values.siteId === 'all') {
        updateValues.siteAccessInfo = {
          isGlobal: true,
        };
      }
    }

    roleAndPermissionsService
      .update_role(updateValues)
      .then((res) => {
        if (res && res.status) {
          this.handleGetRolesList();
          toast.success(CUSTOM_ROLE.UPDATE_ROLE);
          this.setState({ showUpdateRole: false });
        }
        this.handleLoader(false);
      })
      .catch((errResponse) => {
        this.handleLoader(false);
        if ('message' in errResponse) {
          toast.error(errResponse.message);
        } else {
          toast.error(API_FAILURE_ERROR);
        }
      });
  };

  handleShowUpdateRole = (status, params) => {
    if (status) {
      this.getRoleAndPermissions(status, params);
    } else {
      this.setState({ showUpdateRole: status });
    }
  };

  getRoleAndPermissions = (status, params) => {
    const { userTypesList } = this.state;
    this.handleLoader(true);
    roleAndPermissionsService
      .get_permissions_data()
      .then((res) => {
        roleAndPermissionsService
          .get_role(params)
          .then((roleResp) => {
            if (res && res.data && res.data.length) {
              const additionalPermissionsListForEdit = res.data;
              const permissionsListFilterForEdit =
                additionalPermissionsListForEdit.filter((item) => {
                  item.permissions = item.permissions
                    .filter((it) => !it.isDefault)
                    .filter((itm) => itm.isHidden !== true);
                  return item;
                });

              const indx = permissionsListFilterForEdit.findIndex(
                (item) =>
                  item.userTypes.typeId.toLowerCase() === params.userTypeId
              );

              this.setState({
                editSelectedPermissionList: roleResp.data
                  .filter((item) => item.isSelected)
                  .filter((items) =>
                    permissionsListFilterForEdit[indx].permissions.some(
                      (perm) => perm.permissionName === items.permissionName
                    )
                  )
                  .map((item) => item.permissionName),
                editTypesList: permissionsListFilterForEdit
                  .map((item) => {
                    const serialNumber =
                      userTypesList.indexOf(item.userTypes.typeName) + 1;
                    return {
                      isSelected:
                        item.userTypes.typeId.toLowerCase() ===
                        params.userTypeId,
                      value: item.userTypes.typeId.toLowerCase(),
                      label:
                        item.userTypes.typeName.toLowerCase()[0].toUpperCase() +
                        item.userTypes.typeName.toLowerCase().substring(1),
                      serialNumber,
                    };
                  })
                  .sort((a, b) => a.serialNumber - b.serialNumber),
                editRoleName: params.roleName,
                editRoleId: params.roleId,
                siteAccessInfo: params.siteAccessInfo,
              });
              const permissionUiList = permissionsListFilterForEdit.map(
                (item) => {
                  const permissionList = JSON.parse(
                    JSON.stringify(item.permissions)
                  );

                  const groupByModuleName = Object.groupBy(
                    permissionList,
                    ({ moduleName }) => moduleName
                  );
                  const groupByObjectKey = Object.keys(groupByModuleName).map(
                    (itemname) => ({
                      name: itemname,
                      list: groupByModuleName[itemname],
                    })
                  );

                  return {
                    typeName: item.userTypes.typeName,
                    typeId: item.userTypes.typeId,
                    permissions: groupByObjectKey,
                  };
                }
              );

              this.setState({
                editPermissionList: permissionUiList,
                showUpdateRole: status,
              });
              this.handleLoader(false);
            }
          })
          .catch((errResponse) => {
            this.handleLoader(false);
            if ('message' in errResponse) {
              toast.error(errResponse.message);
            } else {
              toast.error(API_FAILURE_ERROR);
            }
          });
      })
      .catch((errResponse) => {
        this.handleLoader(false);
        if ('message' in errResponse) {
          toast.error(errResponse.message);
        } else {
          toast.error(API_FAILURE_ERROR);
        }
      });
  };

  handleRoleDeletion = (roleId) => {
    this.handleLoader(true);
    roleAndPermissionsService
      .delete_role(roleId)
      .then((res) => {
        if (res && res.status) {
          this.handleGetRolesList();
          toast.success(CUSTOM_ROLE.DELETE_ROLE);
        }
        this.handleLoader(false);
      })
      .catch((errResponse) => {
        this.handleLoader(false);
        if ('message' in errResponse) {
          toast.error(errResponse.message);
        } else {
          toast.error(API_FAILURE_ERROR);
        }
      });
  };

  render() {
    const {
      isLoading,
      roles,
      showCreateRole,
      showUpdateRole,
      createPermissionList,
      createTypesList,
      editPermissionList,
      editTypesList,
      editSelectedPermissionList,
      editRoleName,
      editRoleId,
      sitesList,
      siteAccessInfo,
      isLoadingRoles,
    } = this.state;

    return (
      <Layout pageHeading={this.pageHeading}>
        {(isLoading || isLoadingRoles) && <Loader contentAreaOnly />}
        <Row>
          <Col>
            <Card>
              <Card.Header>
                <Row className="align-items-center">
                  <Col>
                    <h4 className="m-0">Custom Roles</h4>
                  </Col>
                  <Col className="col-auto">
                    <CreateRole
                      showCreateRole={showCreateRole}
                      handleCreateRole={this.handleCreateRole}
                      handleShowCreateRole={this.handleShowCreateRole}
                      createTypesList={createTypesList}
                      createPermissionList={createPermissionList}
                      sitesList={sitesList}
                    />
                  </Col>
                </Row>
              </Card.Header>
              <Card.Body className="p-0">
                <ListGroup
                  variant="flush"
                  className="my-n3 settings-teams-list"
                >
                  {roles.map((cRole, index) => (
                    <ListGroupItem key={index}>
                      <Row className="align-items-center">
                        <Col className="col-5">{cRole.roleName}</Col>
                        <Col className="col-auto ms-auto">
                          <Dropdown
                            className="card-dropdown"
                            style={{
                              position: 'relative',
                              top: '0',
                              right: '0',
                            }}
                            id="edit-dropdown"
                          >
                            <Dropdown.Toggle
                              as={Link}
                              to={{ hash: '#' }}
                              className="dropdown-ellipses dropdown-toggle"
                            >
                              <i className="mdi mdi-dots-horizontal" />
                            </Dropdown.Toggle>
                            <Dropdown.Menu align="left">
                              <Dropdown.Item
                                onClick={() => {
                                  this.handleShowUpdateRole(true, {
                                    roleId: cRole._id,
                                    userTypeId: cRole.userTypeId,
                                    userType: cRole.userType,
                                    roleName: cRole.roleName,
                                    siteAccessInfo: cRole.siteAccessInfo,
                                  });
                                }}
                              >
                                Edit
                              </Dropdown.Item>
                              <DeleteButton
                                deleteProp={() =>
                                  this.handleRoleDeletion(cRole._id)
                                }
                              />
                            </Dropdown.Menu>
                          </Dropdown>
                        </Col>
                      </Row>
                    </ListGroupItem>
                  ))}
                </ListGroup>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        {showUpdateRole && (
          <EditRole
            showUpdateRole={showUpdateRole}
            handleShowUpdateRole={this.handleShowUpdateRole}
            handleUpdateRole={this.handleUpdateRole}
            editTypesList={editTypesList}
            editPermissionList={editPermissionList}
            editSelectedPermissionList={editSelectedPermissionList}
            editRoleName={editRoleName}
            editRoleId={editRoleId}
            selectedUserTypeId={
              editTypesList.filter((typeListItem) => typeListItem.isSelected)[0]
                .value
            }
            sitesList={sitesList}
            siteAccessInfo={siteAccessInfo}
          />
        )}
      </Layout>
    );
  }
}

export default RolesAndPermissions;
